import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import { PropertyCard } from "../components/PropertyCard";
import { propertyPath } from "../lib/slug";

const CATS = ["all", "rural", "urban", "industrial"];

export default function Gallery() {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const filter = searchParams.get("cat") || "all";

  useEffect(() => {
    setLoading(true);
    api.get("/properties").then(({ data }) => setProperties(data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? properties : properties.filter((p) => p.category === filter)),
    [properties, filter]
  );

  const setFilter = (c) => {
    if (c === "all") setSearchParams({});
    else setSearchParams({ cat: c });
  };

  const handleOpen = (p) => navigate(propertyPath(p));

  return (
    <div data-testid="gallery-page">
      <section className="pt-40 pb-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <div className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4">{t(lang, "gallery_title")}</div>
            <h1 className="font-serif-display text-5xl md:text-7xl text-white leading-[0.95] mb-6">{t(lang, "gallery_sub")}</h1>
          </motion.div>

          <div className="flex flex-wrap gap-2 md:gap-4 mt-12 border-t border-[#C5A059]/15 pt-6">
            {CATS.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`text-xs tracking-[0.2em] uppercase px-5 py-2 border transition-all ${
                  filter === c
                    ? "bg-[#C5A059] text-[#0A0F16] border-[#C5A059]"
                    : "border-[#C5A059]/25 text-[#A3A9B2] hover:border-[#C5A059] hover:text-white"
                }`}
                data-testid={`filter-${c}`}
              >
                {c === "all" ? t(lang, "filter_all") : t(lang, `cat_${c}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="text-center py-20 text-[#A3A9B2]">Cargando...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="property-grid">
              {filtered.map((p, i) => (
                <PropertyCard key={p.id} property={p} onOpen={handleOpen} index={i} />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-32 text-[#A3A9B2] font-serif-display text-2xl">—</div>
          )}
        </div>
      </section>
    </div>
  );
}
